import {
    Button,
    Checkbox,
    notification,
    Popconfirm,
    Select,
    Switch,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { FC, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteOrderRemove, getMarketInventoryGetThunk } from "tm-shared/api";
import { OrderType, PaymentForm, ProductOrderItem } from "tm-shared/types";
import { formatDate } from "tm-shared/utils";

import FlexContainer from "@/components/FlexContainer";
import { useAppDispatch } from "@/hooks/redux";
import { useMultiTap } from "@/hooks/useMultiTap";
import { setCheckoutDetails } from "@/store/slices/progress";
import {
    changeCheckoutForm,
    changeCheckoutShipmentDate,
} from "@/store/slices/work/work.slice";

import { CarrierModal } from "../../modals/CarrierModal";
import {
    CBGlobalStyle,
    StyledCBBlock,
    StyledCBText,
    StyledCBTitle,
} from "./Styled";

interface ICheckoutBody {
    orderId: string;
    marketId: string;
    companyName: string;
    address: string;
    fop: string;
    orderDate: string;
    shipmentDate: string;
    customShipmentDate: string;
    paymentDate: string;
    paymentForm: PaymentForm;
    orderType: OrderType;
    offlineMode: boolean;
    payOnDeliveryComment: string;
    payOnDeliveryChecked: boolean;
    isCarrier?: boolean;
    isSelfDelivery?: boolean;
    totalSum: string;
    products: ProductOrderItem[];
    handleResetCheckouts: () => void;
    closed?: boolean;
}

interface CheckboxConfig {
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}

const disabledFormChangeOrderTypes = [OrderType.P, OrderType.R];

export const CheckoutBody: FC<ICheckoutBody> = ({
    orderId,
    marketId,
    companyName,
    address,
    fop,
    orderDate,
    shipmentDate,
    customShipmentDate,
    paymentDate,
    paymentForm,
    orderType,
    offlineMode,
    payOnDeliveryComment,
    payOnDeliveryChecked,
    isCarrier = false,
    isSelfDelivery = false,
    totalSum,
    handleResetCheckouts,
    closed = false,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isModalCarrierOpened, setIsModalCarrierOpened] = useState(false);
    const [currentCarrierData, setCurrentCarrierData] = useState<{
        orderId: string;
        paymentForm: string;
    } | null>(null);
    const [showCommentField, setShowCommentField] =
        useState(!!payOnDeliveryComment);
    const [showDateSelect, setShowDateSelect] = useState(false);

    const dateOptions = useMemo(() => {
        if (!shipmentDate) return [];

        const baseDate = dayjs(shipmentDate, "DD.MM.YYYY");

        const options = Array.from({ length: 7 }, (_, i) => {
            const date = baseDate.add(i, "day");
            const formatted = date.format("YYYY-MM-DD");

            return {
                value: formatted,
                label: date.format("DD.MM.YYYY"),
            };
        });

        return options;
    }, [shipmentDate]);

    const handleDoubleClickShipmentDate = () => {
        if (closed || orderType === "r") return;
        setShowDateSelect(true);
    };

    const handleDateChange = (value: string) => {
        dispatch(changeCheckoutShipmentDate({ orderId, date: value }));
        setShowDateSelect(false);
    };

    const handleTap = useMultiTap(handleDoubleClickShipmentDate);

    const viewProductsPath = useMemo(
        () => `/checkout/${orderId}/order-details?isClosed=${closed}`,
        [orderId, closed],
    );

    const companyInfo = useMemo(
        () => [
            {
                title: "Назва компанії",
                value: companyName,
                position: "top" as const,
            },
            { title: "Адреса", value: address, position: "center" as const },
            {
                title: "Юридична особа",
                value: fop,
                position: "center" as const,
            },
        ],
        [companyName, address, fop],
    );

    const handleCarrierChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                setCurrentCarrierData({ orderId, paymentForm });
                setIsModalCarrierOpened(true);
            } else {
                dispatch(
                    setCheckoutDetails({
                        key: "isCarrier",
                        orderId,
                        checked: false,
                    }),
                );
            }
        },
        [dispatch, orderId, paymentForm],
    );

    const handleCarrierConfirm = useCallback(() => {
        if (currentCarrierData) {
            dispatch(
                setCheckoutDetails({
                    key: "isCarrier",
                    orderId,
                    checked: true,
                }),
            );
        }
        setIsModalCarrierOpened(false);
        setCurrentCarrierData(null);
    }, [currentCarrierData, dispatch, orderId]);

    const handleRemoveOrder = useCallback(async () => {
        try {
            await deleteOrderRemove({ orderId });
            notification.success({ message: "Замовлення успішно скасовано" });
            await handleResetCheckouts();

            if (orderType === "r") {
                await dispatch(
                    getMarketInventoryGetThunk({ marketId }),
                ).unwrap();
            }
        } catch (error) {
            notification.error({
                message: "Помилка при скасуванні замовлення",
                description: error instanceof Error ? error.message : undefined,
            });
        }
    }, [dispatch, orderId, orderType, marketId, handleResetCheckouts]);

    const checkboxes = useMemo<CheckboxConfig[]>(() => {
        const configs: CheckboxConfig[] = [];

        if (paymentForm === "2") {
            configs.push(
                {
                    id: `carrier-checkbox-${orderId}-${paymentForm}`,
                    label: "Відвантаження із залишків ТА (багажник)",
                    checked: isCarrier,
                    onChange: handleCarrierChange,
                },
                {
                    id: `self-delivery-checkbox-${orderId}-${paymentForm}`,
                    label: "Самостійний вивіз ТТ",
                    checked: isSelfDelivery,
                    onChange: (checked) =>
                        dispatch(
                            setCheckoutDetails({
                                key: "isSelfPickup",
                                orderId,
                                checked,
                            }),
                        ),
                },
            );
        }

        configs.push({
            id: `pay-on-delivery-checkbox-${orderId}-${paymentForm}`,
            label:
                paymentForm === "1"
                    ? "Дебіторська заборгованість з першого дня"
                    : isSelfDelivery
                      ? "Оплата по факту"
                      : "Оплата в день доставки",
            checked: payOnDeliveryChecked,
            onChange: (checked) =>
                dispatch(
                    setCheckoutDetails({
                        key: "isPayOnDelivery",
                        orderId,
                        checked,
                    }),
                ),
        });

        return configs;
    }, [
        dispatch,
        orderId,
        paymentForm,
        isCarrier,
        isSelfDelivery,
        payOnDeliveryChecked,
        handleCarrierChange,
    ]);

    const handleCommentCheckboxChange = useCallback(
        (checked: boolean) => {
            setShowCommentField(checked);
            if (!checked) {
                dispatch(
                    setCheckoutDetails({
                        key: "comment",
                        orderId,
                        checked: false,
                        comment: "",
                    }),
                );
            }
        },
        [dispatch, orderId],
    );

    const renderCheckboxBlock = useCallback(
        () => (
            <FlexContainer
                flexDirection="column"
                gap="12px"
                alignItems="flex-start"
            >
                {checkboxes.map(
                    ({ id, label, checked, disabled, onChange }) => (
                        <FlexContainer
                            key={id}
                            justifyContent="flex-start"
                            gap="4px"
                        >
                            <Checkbox
                                id={id}
                                checked={checked}
                                disabled={disabled}
                                onChange={(e) => onChange(e.target.checked)}
                            />
                            <StyledCBTitle as="label" htmlFor={id}>
                                {label}
                            </StyledCBTitle>
                        </FlexContainer>
                    ),
                )}
                <FlexContainer justifyContent="flex-start" gap="4px">
                    <Checkbox
                        id={`comment-checkbox-${orderId}`}
                        checked={showCommentField}
                        onChange={(e) =>
                            handleCommentCheckboxChange(e.target.checked)
                        }
                    />
                    <StyledCBTitle
                        as="label"
                        htmlFor={`comment-checkbox-${orderId}`}
                    >
                        Коментар
                    </StyledCBTitle>
                </FlexContainer>
                {showCommentField && (
                    <TextArea
                        maxLength={256}
                        value={payOnDeliveryComment}
                        onChange={(e) =>
                            dispatch(
                                setCheckoutDetails({
                                    key: "comment",
                                    orderId,
                                    checked: true,
                                    comment: e.target.value,
                                }),
                            )
                        }
                    />
                )}
            </FlexContainer>
        ),
        [
            checkboxes,
            showCommentField,
            payOnDeliveryComment,
            dispatch,
            orderId,
            handleCommentCheckboxChange,
        ],
    );

    const renderDisabledCheckbox = useCallback(
        (label: string, checked?: boolean) => (
            <FlexContainer gap="10px" key={label}>
                <Checkbox disabled checked={checked} aria-label={label} />
                <StyledCBTitle>{label}</StyledCBTitle>
            </FlexContainer>
        ),
        [],
    );

    return (
        <FlexContainer flexDirection="column">
            <CBGlobalStyle />
            <CarrierModal
                isModalCarrierOpened={isModalCarrierOpened}
                onConfirmCarrier={handleCarrierConfirm}
                onCancelCarrier={() => setIsModalCarrierOpened(false)}
            />

            <FlexContainer
                flexDirection="column"
                width="100%"
                style={{ padding: "16px 0" }}
            >
                {companyInfo.map(({ title, value, position }) => (
                    <StyledCBBlock key={title} position={position}>
                        <StyledCBTitle>{title}</StyledCBTitle>
                        <StyledCBText>{value || "-"}</StyledCBText>
                    </StyledCBBlock>
                ))}

                <StyledCBBlock position="center">
                    <FlexContainer
                        justifyContent="space-between"
                        width="100%"
                        alignItems="flex-start"
                    >
                        <FlexContainer
                            flexDirection="column"
                            alignItems="flex-start"
                            gap="6px"
                        >
                            <StyledCBTitle>Дата замовлення</StyledCBTitle>
                            <StyledCBText>{formatDate(orderDate)}</StyledCBText>
                        </FlexContainer>
                        {orderType !== "r" && (
                            <FlexContainer
                                flexDirection="column"
                                width="45%"
                                alignItems="flex-start"
                                gap="6px"
                                onTouchStart={handleTap}
                            >
                                <StyledCBTitle>Дата поставки</StyledCBTitle>
                                {showDateSelect ? (
                                    <Select
                                        style={{ width: "100%" }}
                                        value={
                                            customShipmentDate !== "-"
                                                ? customShipmentDate
                                                : shipmentDate
                                        }
                                        options={dateOptions}
                                        onChange={handleDateChange}
                                        onBlur={() => setShowDateSelect(false)}
                                        autoFocus
                                        open
                                    />
                                ) : (
                                    <StyledCBText>
                                        {customShipmentDate &&
                                        customShipmentDate !== "-"
                                            ? customShipmentDate
                                            : shipmentDate}
                                    </StyledCBText>
                                )}
                            </FlexContainer>
                        )}
                    </FlexContainer>
                </StyledCBBlock>

                {orderType !== "r" && (
                    <StyledCBBlock position="center">
                        <FlexContainer
                            justifyContent="space-between"
                            width="100%"
                        >
                            <FlexContainer
                                flexDirection="column"
                                alignItems="flex-start"
                                gap="6px"
                            >
                                <StyledCBTitle>Дата оплати</StyledCBTitle>
                                <StyledCBText>{paymentDate}</StyledCBText>
                            </FlexContainer>

                            <FlexContainer
                                flexDirection="column"
                                width="45%"
                                alignItems="flex-start"
                                gap="6px"
                            >
                                <StyledCBTitle>Тип оплати</StyledCBTitle>
                                <Switch
                                    className="switch-form"
                                    checkedChildren="Форма 2"
                                    unCheckedChildren="Форма 1"
                                    checked={paymentForm === "2"}
                                    onChange={() =>
                                        dispatch(changeCheckoutForm(orderId))
                                    }
                                    disabled={
                                        closed ||
                                        disabledFormChangeOrderTypes.includes(
                                            orderType,
                                        )
                                    }
                                />
                            </FlexContainer>
                        </FlexContainer>
                    </StyledCBBlock>
                )}

                {orderType !== "r" && (
                    <StyledCBBlock position="center">
                        {!closed ? (
                            renderCheckboxBlock()
                        ) : (
                            <FlexContainer
                                gap="10px"
                                flexDirection="column"
                                alignItems="flex-start"
                                width="100%"
                            >
                                {paymentForm === "2" &&
                                    renderDisabledCheckbox(
                                        "Відвантаження із залишків ТА (багажник)",
                                        isCarrier,
                                    )}
                                {paymentForm === "2" &&
                                    renderDisabledCheckbox(
                                        "Самостійний вивіз ТТ",
                                        isSelfDelivery,
                                    )}
                                {renderDisabledCheckbox(
                                    paymentForm === "1"
                                        ? "Дебіторська заборгованість з першого дня"
                                        : isSelfDelivery
                                          ? "Оплата по факту"
                                          : "Оплата в день доставки",
                                    payOnDeliveryChecked,
                                )}
                                {renderDisabledCheckbox(
                                    "Коментар",
                                    !!payOnDeliveryComment?.length,
                                )}
                                {!!payOnDeliveryComment?.length && (
                                    <TextArea
                                        disabled
                                        value={payOnDeliveryComment}
                                        aria-label="Коментар до оплати"
                                        rows={3}
                                    />
                                )}
                            </FlexContainer>
                        )}
                    </StyledCBBlock>
                )}

                {orderType !== "r" && (
                    <StyledCBBlock position="center">
                        <FlexContainer
                            justifyContent="space-between"
                            width="100%"
                        >
                            <StyledCBTitle>Сума</StyledCBTitle>
                            <StyledCBText>{totalSum} грн</StyledCBText>
                        </FlexContainer>
                    </StyledCBBlock>
                )}

                <Button
                    type="primary"
                    style={{ width: "100%", height: "50px", marginTop: "16px" }}
                    onClick={() => navigate(viewProductsPath)}
                    aria-label="Переглянути товари"
                >
                    Переглянути товари
                </Button>

                {!offlineMode && !closed && (
                    <Popconfirm
                        title="Ви впевнені?"
                        description="Скасування замовлення є незворотньою дією"
                        onConfirm={handleRemoveOrder}
                        okText="Так"
                        cancelText="Ні"
                    >
                        <Button
                            danger
                            style={{
                                height: "50px",
                                width: "100%",
                                marginTop: "8px",
                            }}
                        >
                            Скасувати замовлення
                        </Button>
                    </Popconfirm>
                )}
            </FlexContainer>
        </FlexContainer>
    );
};
